import { format as fnsFormat, parse as fnsParse } from "date-fns";
import { fr } from "date-fns/locale";

export const format = (date, format) => fnsFormat(date, format, { locale: fr });

export const utcNow = () =>
  fnsFormat(
    new Date(new Date().toISOString().slice(0, -1)),
    "yyyy-MM-dd HH:mm:ss"
  );

export const parse = (date, format = "yyyy-MM-dd") =>
  fnsParse(date, format, new Date());

/**
 * Timezone conversion utilities for FluidIT PWA
 *
 * The server stores and expects timestamps in UTC format: "YYYY-MM-DD HH:MM:SS"
 * Users should see and input times in their local timezone
 * These utilities handle the conversion between UTC and local time
 */

/**
 * Convert UTC timestamp string to local Date object
 * @param {string} utcTimestamp - UTC timestamp in format "YYYY-MM-DD HH:MM:SS"
 * @returns {Date|null} - Local Date object or null if invalid
 */
export const utcToLocal = (utcTimestamp) => {
  if (!utcTimestamp) return null;

  try {
    // Parse the UTC timestamp by appending 'Z' to indicate UTC
    // Handle both "YYYY-MM-DD HH:MM:SS" and "YYYY-MM-DDTHH:MM:SS" formats
    const normalizedTimestamp = utcTimestamp.replace(" ", "T");
    const utcDate = new Date(
      normalizedTimestamp + (normalizedTimestamp.endsWith("Z") ? "" : "Z")
    );

    if (isNaN(utcDate.getTime())) {
      return null;
    }

    return utcDate;
  } catch (error) {
    console.warn("Failed to parse UTC timestamp:", utcTimestamp, error);
    return null;
  }
};

/**
 * Convert local Date object to UTC timestamp string for server
 * @param {Date} localDate - Local Date object
 * @returns {string} - UTC timestamp in format "YYYY-MM-DD HH:MM:SS"
 */
export const localToUtc = (localDate) => {
  if (
    !localDate ||
    !(localDate instanceof Date) ||
    isNaN(localDate.getTime())
  ) {
    return "";
  }

  try {
    // Get UTC components directly from the Date object
    const year = localDate.getUTCFullYear();
    const month = String(localDate.getUTCMonth() + 1).padStart(2, "0");
    const day = String(localDate.getUTCDate()).padStart(2, "0");
    const hours = String(localDate.getUTCHours()).padStart(2, "0");
    const minutes = String(localDate.getUTCMinutes()).padStart(2, "0");
    const seconds = String(localDate.getUTCSeconds()).padStart(2, "0");

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  } catch (error) {
    console.warn("Failed to convert local date to UTC:", localDate, error);
    return "";
  }
};

/**
 * Format a UTC timestamp for display in local timezone using French locale
 * @param {string} utcTimestamp - UTC timestamp in format "YYYY-MM-DD HH:MM:SS"
 * @returns {string} - Formatted local time string
 */
export const formatUtcForLocalDisplay = (utcTimestamp) => {
  const localDate = utcToLocal(utcTimestamp);
  if (!localDate) return "";

  try {
    return localDate.toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch (error) {
    console.warn(
      "Failed to format UTC timestamp for display:",
      utcTimestamp,
      error
    );
    return utcTimestamp;
  }
};

/**
 * Parse local time components from UTC timestamp
 * @param {string} utcTimestamp - UTC timestamp in format "YYYY-MM-DD HH:MM:SS"
 * @returns {Object} - Object with hour and minute in local time
 */
export const parseLocalTimeFromUtc = (utcTimestamp) => {
  const localDate = utcToLocal(utcTimestamp);
  if (!localDate) return { hour: 0, minute: 0 };

  try {
    return {
      hour: localDate.getHours(),
      minute: Math.floor(localDate.getMinutes() / 5) * 5, // Round to nearest 5-minute interval
    };
  } catch (error) {
    console.warn("Failed to parse local time from UTC:", utcTimestamp, error);
    return { hour: 0, minute: 0 };
  }
};

/**
 * Combine original UTC date with new local time components
 * @param {string} originalUtcTimestamp - Original UTC timestamp
 * @param {number} localHour - New hour in local time (0-23)
 * @param {number} localMinute - New minute in local time (0-59)
 * @returns {string} - New UTC timestamp in format "YYYY-MM-DD HH:MM:SS"
 */
export const combineUtcDateWithLocalTime = (
  originalUtcTimestamp,
  localHour,
  localMinute
) => {
  if (!originalUtcTimestamp) return "";

  try {
    // Get the original date in local timezone
    const originalLocalDate = utcToLocal(originalUtcTimestamp);
    if (!originalLocalDate) return "";

    // Create new local date with same date but new time
    // Use the local date's year, month, day but set new time in local timezone
    const newLocalDate = new Date(
      originalLocalDate.getFullYear(),
      originalLocalDate.getMonth(),
      originalLocalDate.getDate(),
      localHour,
      localMinute,
      0,
      0
    );

    // Convert back to UTC format for server
    return localToUtc(newLocalDate);
  } catch (error) {
    console.warn(
      "Failed to combine UTC date with local time:",
      originalUtcTimestamp,
      localHour,
      localMinute,
      error
    );
    return "";
  }
};

/**
 * Validate that a local time is reasonable (not in future, not too far in past)
 * @param {string} utcTimestamp - UTC timestamp to validate
 * @returns {Object} - Validation result with isValid and errorMessage
 */
export const validateArrivalTime = (utcTimestamp) => {
  if (!utcTimestamp) {
    return { isValid: false, errorMessage: "Heure d'arrivée requise" };
  }

  try {
    const localDate = utcToLocal(utcTimestamp);
    if (!localDate) {
      return { isValid: false, errorMessage: "Format d'heure invalide" };
    }

    const now = new Date();
    const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);

    if (localDate > now) {
      return {
        isValid: false,
        errorMessage: "L'heure d'arrivée ne peut pas être dans le futur",
      };
    }

    if (localDate < oneDayAgo) {
      return {
        isValid: false,
        errorMessage:
          "L'heure d'arrivée ne peut pas être antérieure à 24 heures",
      };
    }

    return { isValid: true, errorMessage: null };
  } catch (error) {
    console.warn("Failed to validate arrival time:", utcTimestamp, error);
    return { isValid: false, errorMessage: "Erreur de validation de l'heure" };
  }
};
